const FORBIDDEN_URL_CHARACTERS = /[\u0000-\u0020\u007f\\]/;
const FORBIDDEN_URL_ENCODINGS = /%(?:0[0-9a-f]|1[0-9a-f]|5c|7f)/i;
const ABSOLUTE_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export type SafeMediaKind = 'image' | 'video';

const PASSIVE_DATA_MIME_TYPES: Record<SafeMediaKind, ReadonlySet<string>> = {
  image: new Set([
    'image/avif',
    'image/bmp',
    'image/gif',
    'image/heic',
    'image/heif',
    'image/jpeg',
    'image/png',
    'image/vnd.microsoft.icon',
    'image/webp',
    'image/x-icon',
  ]),
  video: new Set([
    'video/3gpp',
    'video/mp4',
    'video/ogg',
    'video/quicktime',
    'video/webm',
    'video/x-m4v',
  ]),
};

const normalizeUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  if (
    !normalized ||
    FORBIDDEN_URL_CHARACTERS.test(normalized) ||
    FORBIDDEN_URL_ENCODINGS.test(normalized)
  ) {
    return null;
  }

  return normalized;
};

const getCurrentOrigin = (): string | null => {
  if (typeof window === 'undefined' || !window.location?.origin || window.location.origin === 'null') {
    return null;
  }
  return window.location.origin;
};

const isStrictSameOriginPath = (value: string): boolean => {
  const origin = getCurrentOrigin();
  if (!origin || ABSOLUTE_SCHEME.test(value) || value.startsWith('//')) {
    return false;
  }

  try {
    const parsed = new URL(value, `${origin}/`);
    return (
      parsed.origin === origin &&
      parsed.username === '' &&
      parsed.password === ''
    );
  } catch {
    return false;
  }
};

const isLocalBlobUrl = (value: string): boolean => {
  const origin = getCurrentOrigin();
  if (!origin || !value.startsWith('blob:')) {
    return false;
  }

  try {
    const inner = new URL(value.slice('blob:'.length));
    return (
      (inner.protocol === 'http:' || inner.protocol === 'https:') &&
      inner.origin === origin &&
      inner.username === '' &&
      inner.password === ''
    );
  } catch {
    return false;
  }
};

const isPassiveDataUrl = (value: string, kind: SafeMediaKind): boolean => {
  const match = /^data:([^;,]+)(?:;base64)?,/i.exec(value);
  if (!match) {
    return false;
  }

  return PASSIVE_DATA_MIME_TYPES[kind].has(match[1].toLowerCase());
};

/**
 * Retorna a URL original normalizada somente quando ela aponta para HTTPS.
 * Credenciais embutidas e caracteres com parsing ambíguo são recusados.
 */
export function getSafeHttpsUrl(value: unknown): string | null {
  const normalized = normalizeUrl(value);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(normalized);
    if (
      parsed.protocol !== 'https:' ||
      !parsed.hostname ||
      parsed.username !== '' ||
      parsed.password !== ''
    ) {
      return null;
    }
  } catch {
    return null;
  }

  return normalized;
}

/**
 * Permite navegação apenas para HTTPS absoluto ou caminho relativo que continue
 * estritamente no origin atual. `data:`, `blob:` e URLs protocol-relative são
 * recusadas porque podem transformar conteúdo de mídia em documento ativo.
 */
export function getSafeNavigationUrl(value: unknown): string | null {
  const normalized = normalizeUrl(value);
  if (!normalized) {
    return null;
  }

  if (isStrictSameOriginPath(normalized)) {
    return normalized;
  }

  return getSafeHttpsUrl(normalized);
}

/**
 * Valida uma fonte usada exclusivamente por elementos `img` ou `video`.
 * Além de HTTPS, aceita caminho same-origin, blob criado pelo origin atual e
 * data URL somente com MIME passivo compatível com o tipo do elemento.
 */
export function getSafeMediaUrl(value: unknown, kind: SafeMediaKind): string | null {
  const normalized = normalizeUrl(value);
  if (!normalized || normalized.startsWith('//')) {
    return null;
  }

  if (normalized.startsWith('data:')) {
    return isPassiveDataUrl(normalized, kind) ? normalized : null;
  }

  if (normalized.startsWith('blob:')) {
    return isLocalBlobUrl(normalized) ? normalized : null;
  }

  if (isStrictSameOriginPath(normalized)) {
    return normalized;
  }

  const httpsUrl = getSafeHttpsUrl(normalized);
  if (httpsUrl) {
    return httpsUrl;
  }

  const origin = getCurrentOrigin();
  if (!origin) {
    return null;
  }

  try {
    const parsed = new URL(normalized);
    if (
      parsed.protocol === 'http:' &&
      parsed.origin === origin &&
      parsed.username === '' &&
      parsed.password === ''
    ) {
      return normalized;
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Política para `href` com atributo `download`: reutiliza a validação de mídia,
 * mas nunca permite `data:`. Blob é aceito apenas quando pertence ao origin
 * atual; demais destinos precisam ser HTTPS ou caminho same-origin estrito.
 */
export function getSafeMediaDownloadUrl(
  value: unknown,
  kind: SafeMediaKind,
): string | null {
  const mediaUrl = getSafeMediaUrl(value, kind);
  if (!mediaUrl || mediaUrl.startsWith('data:')) {
    return null;
  }

  if (mediaUrl.startsWith('blob:')) {
    return mediaUrl;
  }

  return getSafeNavigationUrl(mediaUrl);
}
